"use client";

import { useState } from "react";
import { 
  Headphones, 
  Building2, 
  Phone, 
  Clock, 
  ArrowRight, 
  CheckCircle2, 
  ChevronRight,
  TrendingUp,
  Award
} from "lucide-react";
import { TelecallingLead } from "@/lib/demos/telecallingDb";
import { TELECALLING_STAGES } from "@/lib/demos/telecallingData";

interface TelecallingKanbanProps {
  leads: TelecallingLead[];
  onSelectLead: (lead: TelecallingLead) => void;
  onRequestStageAdvance: (lead: TelecallingLead, targetStage: string) => void;
  onDirectStageMove: (leadId: number, targetStage: string) => Promise<void>;
  currency: "INR" | "USD";
}

export function TelecallingKanban({
  leads,
  onSelectLead,
  onRequestStageAdvance,
  onDirectStageMove,
  currency,
}: TelecallingKanbanProps) {
  const [draggedLeadId, setDraggedLeadId] = useState<number | null>(null);
  const sym = currency === "USD" ? "$" : "₹";

  function handleDragStart(e: React.DragEvent, leadId: number) {
    e.dataTransfer.setData("text/plain", String(leadId));
    setDraggedLeadId(leadId);
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
  }

  async function handleDrop(e: React.DragEvent, targetStage: string) {
    e.preventDefault();
    const idStr = e.dataTransfer.getData("text/plain");
    const leadId = Number(idStr);
    setDraggedLeadId(null);

    if (!leadId) return;
    const lead = leads.find((l) => l.id === leadId);
    if (!lead || lead.stage === targetStage) return;

    // If dragging into Demo Scheduled or Deal Won, open modal for required inputs
    if (targetStage === "Demo / Discovery Scheduled" || targetStage === "Deal Closed / Retainer Won") {
      onRequestStageAdvance(lead, targetStage);
    } else {
      await onDirectStageMove(leadId, targetStage);
    }
  }

  // Get next logical stage for 1-click advance
  function getNextStage(currentStage: string): string | null {
    const idx = TELECALLING_STAGES.findIndex((s) => s.name === currentStage);
    if (idx >= 0 && idx < TELECALLING_STAGES.length - 2) {
      return TELECALLING_STAGES[idx + 1].name;
    }
    return null;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 overflow-x-auto pb-6">
      {TELECALLING_STAGES.map((stageObj) => {
        const stageLeads = leads.filter((l) => l.stage === stageObj.name);

        return (
          <div
            key={stageObj.id}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, stageObj.name)}
            className="flex flex-col rounded-2xl bg-slate-900/60 border border-slate-800 p-3 min-w-[250px] transition-colors"
          >
            {/* Column Header */}
            <div className="flex items-center justify-between pb-3 mb-3 border-b border-slate-800">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-cyan-400" />
                <h4 className="text-xs font-bold text-slate-200 line-clamp-1">{stageObj.name}</h4>
              </div>
              <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-full bg-slate-800 text-slate-400">
                {stageLeads.length}
              </span>
            </div>

            {/* Column Body / Cards */}
            <div className="space-y-3 flex-1 overflow-y-auto max-h-[calc(100vh-280px)] pr-1">
              {stageLeads.length === 0 ? (
                <div className="py-8 text-center border-2 border-dashed border-slate-800/60 rounded-xl">
                  <p className="text-[11px] text-slate-500 font-medium">Drop leads here</p>
                </div>
              ) : (
                stageLeads.map((lead) => {
                  const nextStage = getNextStage(lead.stage);

                  // Disposition badge styles
                  const isInterested = lead.disposition === "interested";
                  const isDnd = lead.disposition === "dnd";
                  const isCallback = lead.disposition === "callback";

                  return (
                    <div
                      key={lead.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, lead.id)}
                      onClick={() => onSelectLead(lead)}
                      className={`p-3.5 rounded-xl bg-slate-950 border border-slate-800 hover:border-cyan-500/40 cursor-pointer transition-all hover:shadow-lg hover:shadow-cyan-950/20 group relative ${
                        draggedLeadId === lead.id ? "opacity-40" : ""
                      }`}
                    >
                      {/* Top Bar: Lead No & Disposition */}
                      <div className="flex items-center justify-between gap-1 mb-2">
                        <span className="text-[10px] font-mono text-cyan-400 font-semibold bg-cyan-500/10 px-1.5 py-0.5 rounded border border-cyan-500/20">
                          {lead.lead_no}
                        </span>
                        <span
                          className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                            isInterested
                              ? "bg-emerald-500/15 text-emerald-300"
                              : isDnd
                              ? "bg-rose-500/15 text-rose-300"
                              : isCallback
                              ? "bg-amber-500/15 text-amber-300"
                              : "bg-blue-500/15 text-blue-300"
                          }`}
                        >
                          {lead.disposition}
                        </span>
                      </div>

                      {/* Company & Prospect Name */}
                      <h5 className="text-xs font-bold text-white group-hover:text-cyan-300 transition-colors line-clamp-1">
                        {lead.company_name}
                      </h5>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        {lead.prospect_name}
                      </p>

                      {/* Campaign Track Pill */}
                      <div className="mt-2 text-[10px] text-slate-400 font-medium line-clamp-1">
                        {lead.campaign_name}
                      </div>

                      {/* Pod Details & Retainer */}
                      <div className="flex items-center justify-between text-[10px] font-semibold text-slate-300 mt-2.5 pt-2 border-t border-slate-800/80">
                        <span className="text-cyan-400 font-mono">
                          {lead.seats_count} Seats ({lead.shift_type.toUpperCase()})
                        </span>
                        <span className="text-white font-bold">
                          {sym}{lead.monthly_retainer.toLocaleString()}
                        </span>
                      </div>

                      {/* Metrics: SDR & QA Score */}
                      <div className="flex items-center justify-between text-[9px] text-slate-500 mt-1.5">
                        <span>SDR: {lead.assigned_agent.split(" ")[0]}</span>
                        <span className="text-emerald-400 font-medium">QA {lead.qa_score}%</span>
                      </div>

                      {/* Scheduled Meeting slot preview if present */}
                      {lead.demo_meeting_time && (
                        <div className="mt-2 p-1.5 rounded bg-cyan-950/40 border border-cyan-500/20 text-[9px] text-cyan-300 line-clamp-1">
                          📅 {lead.demo_meeting_time}
                        </div>
                      )}

                      {/* Quick Advance Button */}
                      {nextStage && (
                        <div className="mt-3 pt-2 border-t border-slate-800/60 flex justify-end">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onRequestStageAdvance(lead, nextStage);
                            }}
                            className="px-2.5 py-1 rounded-lg bg-cyan-500/10 hover:bg-cyan-500 text-cyan-300 hover:text-white text-[10px] font-bold tracking-wide border border-cyan-500/20 hover:border-transparent transition-all flex items-center gap-1"
                          >
                            <span>Advance</span>
                            <ChevronRight className="w-3 h-3" />
                          </button>
                        </div>
                      )}

                    </div>
                  );
                })
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
